import type { Secret, SecretSource } from '../../shared/interfaces/secrets.interface';

/**
 * View model for one row of the secrets list. Produced purely from the API
 * {@link Secret} plus the project's {@link SecretSource} list — no component or
 * service state — so it is trivially unit-testable. The template binds the
 * precomputed fields directly (resolved source name, security icon/label)
 * rather than calling component methods per cell.
 *
 * Mirrors the connections list's `ConnectionDisplay` mapper (SCRUM-761).
 */
export interface SecretDisplay {
  secretId: number;
  sourceId: number;
  name: string;
  /** Display name of the backing source, resolved from the sources list. */
  sourceName: string;
  gettable: boolean;
  /** `mat-icon` name for the security badge. */
  securityIcon: 'visibility' | 'visibility_off';
  /** Human label for the security badge. */
  securityLabel: 'Readable' | 'Write-only';
  /** The original API object, handed back to edit/delete/view handlers. */
  original: Secret;
}

/** Fallback shown when a secret's backing source can't be found. */
const UNKNOWN_SOURCE = 'Unknown';

/**
 * Resolve the display name of the source backing a secret. Matches the list's
 * long-standing behavior of resolving against the loaded sources rather than
 * trusting the (possibly stale) `sourceName` embedded on the secret; falls back
 * to `'Unknown'` when the source is missing.
 */
export function resolveSourceName(
  sourceId: number,
  sources: SecretSource[],
): string {
  return sources.find((s) => s.sourceId === sourceId)?.name ?? UNKNOWN_SOURCE;
}

/** Index source names by `sourceId` for O(1) lookup when mapping many secrets. */
function indexSourceNames(sources: SecretSource[]): Map<number, string> {
  const namesById = new Map<number, string>();
  for (const source of sources) {
    namesById.set(source.sourceId, source.name);
  }
  return namesById;
}

/** Build one display row from a secret and its already-resolved source name. */
function toDisplay(secret: Secret, sourceName: string): SecretDisplay {
  // `gettable` ("Readable") is an admin choice per secret — whether a step can
  // read the value back — independent of the source type.
  return {
    secretId: secret.secretId,
    sourceId: secret.sourceId,
    name: secret.name,
    sourceName,
    gettable: secret.gettable,
    securityIcon: secret.gettable ? 'visibility' : 'visibility_off',
    securityLabel: secret.gettable ? 'Readable' : 'Write-only',
    original: secret,
  };
}

/** Map a single secret to its display row. */
export function buildSecretDisplay(
  secret: Secret,
  sources: SecretSource[],
): SecretDisplay {
  return toDisplay(secret, resolveSourceName(secret.sourceId, sources));
}

/**
 * Map the full secrets list to display rows. Indexes the sources once so the
 * mapping is O(secrets + sources) rather than O(secrets × sources) — this runs
 * on every store update.
 */
export function buildSecretDisplays(
  secrets: Secret[],
  sources: SecretSource[],
): SecretDisplay[] {
  const namesById = indexSourceNames(sources);
  return secrets.map((secret) =>
    toDisplay(secret, namesById.get(secret.sourceId) ?? UNKNOWN_SOURCE),
  );
}
